import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  User, 
  Home,
  CreditCard,
  Plus
} from 'lucide-react';
import billingAPI from '../services/billingAPI';

const MonthlyBillSummary = () => {
  const { roomId, month, year } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchBillDetails();
  }, [roomId, month, year]);

  const fetchBillDetails = async () => {
    try {
      setLoading(true);
      const response = await billingAPI.getMonthlyBill(roomId, month, year);
      const billData = response.data;

      setBill(billData.bill || null);

      if (billData.bill) {
        setRoom({
          room_number: billData.bill.room_number,
          tenant_name: billData.bill.tenant_name
        });
      }
    } catch (err) {
      console.error('Error fetching bill details:', err);
      setError('Failed to load bill details');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    try {
      setSubmitting(true);

      const paymentData = {
        room_id: parseInt(roomId, 10),
        payment_month: parseInt(month, 10),
        payment_year: parseInt(year, 10),
        amount: parseFloat(paymentForm.amount),
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes || `Payment for ${monthNames[parseInt(month, 10) - 1]} ${year}`
      };

      await billingAPI.recordPayment(paymentData);

      alert('Payment recorded successfully!');
      navigate('/rooms');
    } catch (err) {
      console.error('Error recording payment:', err);
      alert('Failed to record payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/rooms')}
            className="btn-secondary"
          >
            Back to Rooms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/rooms')}
            className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
              {monthNames[parseInt(month, 10) - 1]} {year} Bill Summary
            </h1>
            <p className="text-secondary-600 dark:text-secondary-300">
              Room {room?.room_number} - {room?.tenant_name}
            </p>
          </div>
        </div>
      </div>

      {/* Bill Details Card */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-200" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Bill Details</h2>
            <p className="text-secondary-600 dark:text-secondary-300">Monthly charges and current status</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Room Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Home className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-300">Room</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{room?.room_number}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <User className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-300">Tenant</p>
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{room?.tenant_name}</p>
              </div>
            </div>
          </div>

          {/* Bill Amounts */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-secondary-600 dark:text-secondary-300">Monthly Rent:</span>
              <span className="font-medium">{formatCurrency(bill?.rent_amount || 0)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-secondary-600 dark:text-secondary-300">Electricity:</span>
              <span className="font-medium">{formatCurrency(bill?.electricity_amount || 0)}</span>
            </div>
            
            <div className="border-t pt-2 border-secondary-100 dark:border-secondary-700">
              <div className="flex justify-between items-center">
                <span className="font-medium text-secondary-900 dark:text-secondary-100">Total Due:</span>
                <span className="font-bold text-lg">{formatCurrency(bill?.total_amount || 0)}</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-secondary-600 dark:text-secondary-300">Total Paid:</span>
              <span className="font-medium text-green-600">{formatCurrency(bill?.total_paid || 0)}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-secondary-600 dark:text-secondary-300">Outstanding:</span>
              <span className="font-medium text-red-600">{formatCurrency(bill?.outstanding || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Payment Form */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
            <Plus className="w-6 h-6 text-green-600 dark:text-green-200" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Record Payment</h2>
            <p className="text-secondary-600 dark:text-secondary-300">Add to tenant's balance (auto-allocates to oldest bills first)</p>
          </div>
        </div>

        <form onSubmit={handlePaymentSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="amount" className="form-label">
                Payment Amount *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 dark:text-secondary-300 w-4 h-4" />
                <input
                  type="number"
                  id="amount"
                  step="0.01"
                  min="0"
                  className="form-input pl-10 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="payment_method" className="form-label">
                Payment Method
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 dark:text-secondary-300 w-4 h-4" />
                <select
                  id="payment_method"
                  className="form-input pl-10 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="card">Card</option>
                  <option value="mobile_money">Mobile Money</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="form-label">
              Notes (Optional)
            </label>
            <textarea
              id="notes"
              rows="2"
              className="form-input bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100"
              placeholder="Additional notes about this payment..."
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? 'Recording...' : 'Record Payment'}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/rooms')}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MonthlyBillSummary;
